<template>
  <b-card :title="$t('pamm_own_invest')">
    <p class="text-muted mb-2">
      {{ $t('pamm_own_invest_description') }}
    </p>
    <b-overlay
      :show="show"
      spinner-variant="primary"
      spinner-type="grow"
      spinner-small
      rounded="sm"
    >
      <template v-if="investments.length > 0">
        <b-table
          :items="investments"
          :fields="fields"
          :per-page="serverPaging ? 0 : pagination.per_page"
          :current-page="serverPaging ? 1 : pagination.current_page"
          responsive
          stacked="md"
          hover
          class="mb-0"
        >
          <template #cell(manager)="data">
            <div class="d-flex align-items-center">
              <b-avatar
                :text="initials(data.item.nick_name)"
                variant="light-success"
                size="34"
                class="flex-shrink-0"
              />
              <div class="mx-1 text-truncate">
                <div class="font-weight-bold text-truncate">
                  {{ data.item.nick_name }}
                </div>
                <small class="text-muted">#{{ data.item.manager_id }}</small>
              </div>
            </div>
          </template>
          <template #cell(pamm_period_id)="data">
            <span class="text-nowrap">#{{ data.item.pamm_period_id }}</span>
          </template>
          <template #cell(status)="data">
            <b-badge :variant="data.item.status ? 'light-success' : 'light-secondary'">
              {{ data.item.status ? $t('active') : $t('not_active') }}
            </b-badge>
          </template>
          <template #cell(action)="data">
            <div class="d-flex flex-wrap align-items-center justify-content-center">
              <b-button
                variant="primary"
                size="sm"
                class="m-25"
                :to="{ name: 'pamm_invest_details', params: { id: data.item.manager_id }, query: { name: data.item.nick_name, investor: data.item.pamm_investor_id } }"
              >
                <feather-icon
                  icon="BarChart2Icon"
                  size="14"
                />
                <span class="mx-25">{{ $t('details') }}</span>
              </b-button>
              <b-button
                variant="outline-primary"
                size="sm"
                class="m-25"
                :href="`${accountDetailsUrl}/${data.item.manager_id}`"
                target="_blank"
              >
                <feather-icon
                  icon="ExternalLinkIcon"
                  size="14"
                />
                <span class="mx-25">{{ $t('account_page') }}</span>
              </b-button>
              <!-- deliberately quiet: leaving early costs a fine -->
              <b-button
                variant="flat-secondary"
                size="sm"
                class="pamm-exit-button m-25"
                :disabled="!data.item.pamm_investor_id || exitPendingId !== null"
                @click="earlyExit(data.item)"
              >
                <b-spinner
                  v-if="exitPendingId === data.item.pamm_investor_id"
                  small
                  type="grow"
                />
                <span v-else>{{ $t('pamm_early_exit') }}</span>
              </b-button>
            </div>
          </template>
        </b-table>
        <div
          v-if="totalRows > pagination.per_page"
          class="d-flex justify-content-center mt-2"
        >
          <b-pagination
            v-model="pagination.current_page"
            :total-rows="totalRows"
            :per-page="pagination.per_page"
            size="sm"
            class="mb-0"
            @input="changePage"
          />
        </div>
      </template>
      <div
        v-else-if="!show"
        class="text-center py-2"
      >
        <p>{{ $t('pamm_no_investments') }}</p>
        <b-button
          :to="{ name: 'pamm_invest' }"
          variant="primary"
        >
          {{ $t('list_pamm_account') }}
        </b-button>
      </div>
    </b-overlay>

    <EarlyExitModal
      ref="earlyExit"
      @exited="getAccounts"
    />
  </b-card>
</template>

<script>
import {
  BOverlay,
  BCard,
  BTable,
  BAvatar,
  BBadge,
  BButton,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import PammApis from '@/modules/pamm/service/apis'
import EarlyExitModal from '../components/early-exit-modal.vue'

const pammApis = new PammApis()

export default {
  components: {
    BOverlay,
    BCard,
    BTable,
    BAvatar,
    BBadge,
    BButton,
    BSpinner,
    BPagination,
    EarlyExitModal,
  },
  data() {
    return {
      accountDetailsUrl: process.env.VUE_APP_PAMM_ACCOUNT_BASE_URL,
      show: true,
      investments: [],
      // true when the api pages the list itself; otherwise the table pages what it got
      serverPaging: false,
      serverTotal: 0,
      pagination: {
        current_page: 1,
        per_page: 10,
      },
      // the investment whose early-exit quote is being fetched
      exitPendingId: null,
    }
  },
  computed: {
    fields() {
      return [
        { key: 'manager', label: this.$t('pamm_manager') },
        { key: 'pamm_period_id', label: this.$t('pamm_period') },
        { key: 'status', label: this.$t('status') },
        { key: 'action', label: this.$t('action'), class: 'text-center' },
      ]
    },
    totalRows() {
      return this.serverPaging ? this.serverTotal : this.investments.length
    },
  },
  mounted() {
    this.getAccounts()
  },
  methods: {
    async getAccounts() {
      this.show = true
      await pammApis.getInvestorList({
        page: this.pagination.current_page,
        per_page: this.pagination.per_page,
      }).then(res => {
        const { results } = res.data
        if (Array.isArray(results)) {
          // newest period first
          this.investments = [...results].sort((a, b) => Number(b.pamm_period_id) - Number(a.pamm_period_id))
          this.serverPaging = false
          return
        }
        this.investments = results?.data ?? []
        this.serverPaging = Boolean(results?.paginate)
        if (this.serverPaging) {
          this.serverTotal = results.paginate.total
          this.pagination.current_page = results.paginate.current_page
          this.pagination.per_page = results.paginate.per_page
        }
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.show = false
      })
    },
    changePage() {
      if (this.serverPaging) this.getAccounts()
    },
    // The dialog opens only after its quote has arrived; until then the row's button spins.
    async earlyExit(item) {
      this.exitPendingId = item.pamm_investor_id
      await this.$refs.earlyExit.open(item.pamm_investor_id, item.nick_name)
      this.exitPendingId = null
    },
    initials(name) {
      const trimmed = (name || '').trim()
      return trimmed ? trimmed.slice(0, 2).toUpperCase() : '#'
    },
  },
}
</script>

<style>
.pamm-button{
  background: linear-gradient(180deg, #068787 0%, #0A9292 48.96%, #035C68 100%) !important ;
  border:none !important ;
}
</style>

<style lang="scss" scoped>
// keeps its width while the spinner replaces the label
.pamm-exit-button {
  min-width: 7rem;
  font-weight: 400;
}
</style>
